import PageWrapper from "../pageWrapper";
import PageBand from "../pageBand";
import { setDocumentTitle } from "../helpers/helpers";

export default function Cookies() {
  setDocumentTitle("Cookie and Tracking Policy");

  return (
    <PageBand>
      <PageWrapper>
        <div id="privacy" className="container">
          <div className="row">
            <div>
              <h3>Cookie and Tracking Policy</h3>
              <h4>Introduction</h4>
              <p>
                TargetSmart Communications ("we", "us", "TargetSmart")
                understands that privacy is important to the data subject
                ("you", "your", "user") and we are committed to being
                transparent about the technologies we use. This Cookie and
                Tracking Policy ("Cookie Policy") explains how and why cookies
                and similar tracking technologies ("Tracking Technologies") may
                be stored on and accessed from your device when you use or visit
                any TargetSmart website or other sites that post a link to this
                Cookie Policy (collectively, "the Sites"). This Cookie Policy
                should be read with our{" "}
                <a
                  href="https://targetsmart.com/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{" "}
                to indicate the types of data and uses of any personal data
                TargetSmart may collect.
              </p>

              <h4>Cookies and Other Tracking Technologies</h4>
              <p>
                A “cookie” is a small text file stored on a personal device
                (phone, tablet, laptop, desktop, etc.) that contains information
                about your online activity. The information stored may include
                your Internet Protocol (“IP Address”) and other information
                identifying the specific device in use.
              </p>

              <p>Other technology we may use includes:</p>
              <ul>
                <li>
                  <strong>Local Storage:</strong> TargetSmart websites may, in
                  some cases, store information locally on any given device.
                  This is done to improve your experience when using our
                  websites by saving your user preferences or enabling website
                  features.
                </li>
                <li>
                  <strong>Web beacons and tracking pixels:</strong> Web beacons
                  or tracking pixels (these terms are interchangeable) are small
                  invisible graphic files generally used to track user behavior,
                  the progression of web traffic, or site conversations. Web
                  pixels allow TargetSmart to collect data on how individuals
                  use our websites. Tracking pixels can also inform TargetSmart
                  about advertisements on third-party platforms based on the
                  activity tracking from your browser or device, then
                  identifying return traffic from third-party platforms such as
                  social media applications and third-party web analytics.
                </li>
                <li>
                  <strong>Other tracking technologies:</strong> TargetSmart may
                  also use other tracking technologies, such as mobile
                  advertising IDs and tags for similar purposes as described in
                  this Cookie Policy. As noted above, we refer to all these
                  technologies as “Tracking Technologies” in this Policy.
                </li>
              </ul>

              <h4>Do Not Track Signals and TargetSmart’s Response</h4>
              <p>
                “Do Not Track” is an optional setting on your web browser that
                allows you to express your specific preferences regarding
                tracking by advertisers and other third parties. PLEASE NOTE:
                Currently, TargetSmart’s websites do not respond to a “Do Not
                Track” signal.
              </p>

              <h4>Opt-Out</h4>
              <p>
                Users may configure a specific web browser to reject some or all
                of the Tracking Technologies discussed in this Policy. Users
                should note that rejecting some cookies may cause them to be
                unable to access all of TargetSmart’s websites or may not enable
                the use of certain features of TargetSmart websites. If you
                would like to learn more about the operation and management of
                cookies, please visit{" "}
                <a
                  href="https://www.aboutcookies.org/"
                  target="_blank"
                  rel="noreferrer"
                >
                  AboutCookies.org
                </a>
                .
              </p>

              <p>
                Additionally, third-party marketing companies may manage opt-out
                and online behavioral advertisement preferences. Using a
                third-party marketing company does not prevent you from
                receiving advertisements but may make the ads you receive more
                generic and less specific to your interests, which are
                identified by your web browsing behaviors. If you delete
                existing cookies, you may delete any opt-out preferences you
                previously set.
              </p>

              <h4>Types of Cookies</h4>
              <p>TargetSmart uses two types of cookies on our website:</p>
              <ul>
                <li>
                  <strong>Session cookies:</strong> Session cookies are deleted
                  or removed when you close your browser.
                </li>
                <li>
                  <strong>Persistent cookies:</strong> Cookies that may remain
                  on your device after you close your browser but expire at a
                  specific time and date.
                </li>
              </ul>

              <p>
                Cookies placed on your device through TargetSmart’s websites are
                either first-party or third-party cookies. First-party cookies
                are placed directly by TargetSmart. Third-party cookies are
                placed on your device through our websites by third parties.
                When you visit a TargetSmart website, certain third parties may
                place cookies on your device to collect information about your
                online activities over time and across different websites or
                online services. For additional information on how a third party
                collects and uses your data, please refer to the privacy notices
                of those third parties.
              </p>

              <h4>How do we use cookies and other tracking technologies?</h4>
              <p>
                TargetSmart uses Tracking Technologies to collect and store
                information for the following purposes:
              </p>
              <ul>
                <li>To identify the geographic source of web requests</li>
                <li>
                  To identify the browser and device type associated with web
                  requests
                </li>
                <li>For analytics and research</li>
                <li>
                  To measure and analyze the audience for each page to
                  subsequently improve the ergonomics, browsing, or visibility
                  of content
                </li>
                <li>
                  To measure the reliability of our websites by analyzing the
                  number of visits to its pages in real-time
                </li>
                <li>
                  To measure ads seen by an individual and the progression the
                  individual used to access the advertisement
                </li>
              </ul>

              <p>
                The data collected will not be kept longer than necessary to
                fulfill the above-mentioned purposes.
              </p>

              <h4>Types of cookies used</h4>
              <p>
                TargetSmart uses several different types of cookies on our
                websites.
              </p>
              <ul>
                <li>
                  <strong>Strictly Necessary Cookies:</strong> These cookies are
                  necessary for the website to function and cannot be switched
                  off in our systems. They are usually only set in response to
                  your actions, which amount to a service request, such as
                  setting your privacy preferences, logging in, or filling in
                  forms. You can set your browser to block or alert you about
                  these cookies, but some parts of the site will not work. These
                  cookies do not store any personally identifiable information.
                </li>
                <li>
                  <strong>Performance Cookies:</strong> These cookies allow
                  TargetSmart to count visits and traffic sources so we can
                  measure and improve our website performance. They help us know
                  which pages are the most and least popular and see how
                  visitors move around the site. All information these cookies
                  collect is aggregated and anonymous. If you do not allow these
                  cookies, we will not know when you have visited our site and
                  will not be able to monitor its performance.
                </li>
                <li>
                  <strong>Functional Cookies:</strong> These cookies enable
                  TargetSmart to provide enhanced functionality and
                  personalization on our websites. They may be set by us or by
                  third-party providers whose services we have added to our
                  pages. If you do not allow these cookies, then some or all of
                  these services may not function properly.
                </li>
                <li>
                  <strong>Targeting Cookies:</strong> These cookies may be set
                  through TargetSmart websites by us or our advertising
                  partners. These cookies may be used to build a profile of your
                  interests and show you relevant adverts on other sites. They
                  do not store personal information directly but are based on
                  uniquely identifying your browser and internet device. If you
                  do not allow these cookies, you will experience less targeted
                  advertising.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PageWrapper>
    </PageBand>
  );
}
